import { useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import MappingCard from './MappingCard';
import type { SourceSystem } from '@types';

interface MappingListProps {
  mappings: Array<{
    id: string;
    name: string;
    source_system: SourceSystem;
    is_default: boolean;
    field_count: number;
    created_at: string;
    updated_at: string;
  }>;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onSetDefault: (id: string) => void;
  onCreate: () => void;
}

const sourceSystemOptions: { value: SourceSystem | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All Systems' },
  { value: 'QUICKBOOKS', label: 'QuickBooks' },
  { value: 'ADP', label: 'ADP' },
  { value: 'PAYCHEX', label: 'Paychex' },
  { value: 'GUSTO', label: 'Gusto' },
  { value: 'WORKDAY', label: 'Workday' },
];

export default function MappingList({
  mappings,
  onEdit,
  onDelete,
  onDuplicate,
  onSetDefault,
  onCreate,
}: MappingListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSystem, setFilterSystem] = useState<SourceSystem | 'ALL'>('ALL');

  // Filter mappings based on search and source system
  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = mapping.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesSystem =
      filterSystem === 'ALL' || mapping.source_system === filterSystem;
    return matchesSearch && matchesSystem;
  });

  // Group by source system
  const groupedMappings = filteredMappings.reduce(
    (acc, mapping) => {
      if (!acc[mapping.source_system]) {
        acc[mapping.source_system] = [];
      }
      acc[mapping.source_system].push(mapping);
      return acc;
    },
    {} as Record<SourceSystem, typeof filteredMappings>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Mapping Configurations
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Configure how fields are mapped from source systems to the V3
            standard format
          </p>
        </div>
        <button
          onClick={onCreate}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Mapping
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search mappings by name..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        {/* Filter by Source System */}
        <div className="sm:w-64">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterSystem}
              onChange={e =>
                setFilterSystem(e.target.value as SourceSystem | 'ALL')
              }
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {sourceSystemOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        Showing {filteredMappings.length} of {mappings.length} mapping
        {mappings.length !== 1 ? 's' : ''}
      </div>

      {/* Mappings Grid or Empty State */}
      {filteredMappings.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          {mappings.length === 0 ? (
            <>
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No mappings configured
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new mapping configuration.
              </p>
              <div className="mt-6">
                <button
                  onClick={onCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Mapping
                </button>
              </div>
            </>
          ) : (
            <>
              <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No mappings found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterSystem('ALL');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
            </>
          )}
        </div>
      ) : filterSystem === 'ALL' ? (
        // Grouped by Source System
        <div className="space-y-8">
          {Object.entries(groupedMappings).map(([system, systemMappings]) => (
            <div key={system}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {system}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({systemMappings.length})
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {systemMappings.map(mapping => (
                  <MappingCard
                    key={mapping.id}
                    mapping={mapping}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onSetDefault={onSetDefault}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Simple Grid (when filtered by system)
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMappings.map(mapping => (
            <MappingCard
              key={mapping.id}
              mapping={mapping}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onSetDefault={onSetDefault}
            />
          ))}
        </div>
      )}
    </div>
  );
}
